import React, { useRef, useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import { makeStyles, ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import { green } from '@material-ui/core/colors';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Icon from '@material-ui/core/Icon';
import CallAudioUploader from './CallAudioUploader';
import { useApi } from 'app/hooks';
import {
	NODE_KINDS,
	NODE_KIND_OPTIONS,
	DEFAULT_NO_REPLY_TIMEOUT_MS,
	DEFAULT_NO_REPLY_MAX_ATTEMPTS
} from './ivrHelpers';

const useStyles = makeStyles(theme => ({
	formControl: {
		marginBottom: theme.spacing(2),
		minWidth: 330
	},
	margin: {
		color: 'white',
		paddingLeft: '14px',
		fontWeight: 'bold',
		paddingRight: '14px',
		paddingTop: '5px',
		paddingBottom: '5px',
		fontSize: '12px'
	},
	sectionTitle: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
		fontSize: '13px',
		fontWeight: 600
	},
	sectionTitleRow: {
		display: 'flex',
		alignItems: 'center',
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1)
	},
	sectionTitleInRow: {
		margin: 0,
		fontSize: '13px',
		fontWeight: 600
	},
	infoIcon: {
		fontSize: 16,
		marginLeft: 6,
		color: theme.palette.text.secondary,
		cursor: 'help'
	}
}));

const theme = createMuiTheme({
	palette: {
		primary: green
	}
});

// One dialog used for both adding and editing a call node.
// Props:
//   isRoot      -> the Welcome menu (no digit / no kind selection)
//   initialNode -> existing node when editing
//   submitNode  -> (data) => void with the node fields
const CallNodeDialog = props => {
	const classes = useStyles(props);
	const { isRoot, initialNode, closeDialog, submitNode, clientId, siblingKeys = [] } = props;
	const { request } = useApi();

	// The Welcome (root) node picks its type the same way an option does, so a
	// flow can start on a menu, a plain audio message, or a straight transfer.
	const [kind, setKind] = useState(initialNode?.nodeKind || NODE_KINDS.MENU);
	const [label, setLabel] = useState(initialNode?.label || initialNode?.title || '');
	const [optionKey, setOptionKey] = useState(initialNode?.optionKey != null ? String(initialNode.optionKey) : '');
	// Shown to the user in seconds; the node/API value is stored in milliseconds.
	const [timeout, setTimeoutValue] = useState(
		initialNode?.noReplyTimeoutMs != null
			? String(initialNode.noReplyTimeoutMs / 1000)
			: String(DEFAULT_NO_REPLY_TIMEOUT_MS / 1000)
	);
	const [maxAttempts, setMaxAttempts] = useState(
		initialNode?.noReplyMaxAttempts != null
			? String(initialNode.noReplyMaxAttempts)
			: String(DEFAULT_NO_REPLY_MAX_ATTEMPTS)
	);
	const [agentId, setAgentId] = useState(initialNode?.agentId != null ? String(initialNode.agentId) : '');
	const [agentType, setAgentType] = useState(initialNode?.agentType != null ? String(initialNode.agentType) : '');
	const [providers, setProviders] = useState([]);
	const [agents, setAgents] = useState([]);
	const [agentsLoading, setAgentsLoading] = useState(false);
	const [liveAgents, setLiveAgents] = useState([]);
	const [liveAgentsLoading, setLiveAgentsLoading] = useState(false);
	const [errors, setErrors] = useState({});

	const audioRef = useRef();
	const noReplyAudioRef = useRef();
	const invalidAudioRef = useRef();
	const transferAudioRef = useRef();
	const holdMusicRef = useRef();

	const needsAudio = kind === NODE_KINDS.MENU || kind === NODE_KINDS.END;
	const isMenu = kind === NODE_KINDS.MENU;
	const isAIAgent = kind === NODE_KINDS.TRANSFER_AGENT;
	const isLiveAgent = kind === NODE_KINDS.TRANSFER_HUMAN;
	const isElevenLabs = String(agentType).toLowerCase() === 'elevenlabs';

	// Load AI providers for the "Provider" (agent_type) dropdown.
	useEffect(() => {
		if (!isAIAgent) return;
		request({
			module: 'aiCallProvider',
			method: 'all',
			payload: {},
			onSuccess: res => setProviders(res.data?.data?.providers || []),
			onError: () => {}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAIAgent]);

	// When the selected provider is ElevenLabs, load its agents for the agent dropdown.
	useEffect(() => {
		if (!isAIAgent || !isElevenLabs) {
			setAgents([]);
			return;
		}
		setAgentsLoading(true);
		request({
			module: 'elevenLabsAgent',
			method: 'all',
			payload: clientId != null ? { clientId } : {},
			onSuccess: res => {
				setAgents(res.data?.data?.agents || []);
				setAgentsLoading(false);
			},
			onError: () => {
				setAgents([]);
				setAgentsLoading(false);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAIAgent, isElevenLabs, clientId]);

	// Load the client's users for the "Go to Human" agent dropdown. Only users
	// who are allowed to take calls (allow_calls === true) are shown; the API is
	// the same one the Users page uses (/client/agents-paginated).
	useEffect(() => {
		if (!isLiveAgent) return;
		setLiveAgentsLoading(true);
		request({
			module: 'CompanyAgent',
			method: 'get_paginated',
			payload: clientId != null ? { client_id: clientId, limit: 200, page: 0 } : { limit: 200, page: 0 },
			onSuccess: res => {
				const list = res.data?.data?.agentList || [];
				setLiveAgents(list.filter(a => a.allow_calls === true));
				setLiveAgentsLoading(false);
			},
			onError: () => {
				setLiveAgents([]);
				setLiveAgentsLoading(false);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLiveAgent, clientId]);

	const handleProviderChange = e => {
		const value = e.target.value;
		setAgentType(value);
		// Only ElevenLabs has agents; clear any selected agent for other providers.
		if (String(value).toLowerCase() !== 'elevenlabs') setAgentId('');
	};

	const handleSubmit = () => {
		const _errors = {};

		// The key is optional (a blank key disables the option). When a key IS
		// entered, it must be unique among its sibling options.
		if (!isRoot && optionKey !== '' && optionKey != null) {
			const taken = (siblingKeys || []).map(String);
			if (taken.includes(String(optionKey))) {
				_errors.optionKey = `Key "${optionKey}" is already used by another option`;
			}
		}
		if (!isRoot && !label) {
			_errors.label = 'Title is required';
		}
		if (isAIAgent && !agentType) {
			_errors.agentType = 'Provider is required';
		}

		let audio = initialNode?.audio || [];
		let noReplyAudio = initialNode?.noReplyAudio || [];
		let invalidAudio = initialNode?.invalidAudio || [];
		let transferAudio = initialNode?.audio || [];
		let holdMusic = initialNode?.holdMusic || [];

		if (needsAudio && audioRef.current) {
			const [audioError, audioData] = audioRef.current.getResult();
			if (audioError) {
				_errors.audio = true;
			} else {
				audio = audioData.audio;
			}
		}

		if (isMenu && noReplyAudioRef.current) {
			// no-reply audio is optional; only capture it when something was uploaded
			const [, noReplyData] = noReplyAudioRef.current.getResult();
			if (noReplyData && noReplyData.audio && noReplyData.audio.length) {
				noReplyAudio = noReplyData.audio;
			} else {
				noReplyAudio = [];
			}
		}

		if (isMenu && invalidAudioRef.current) {
			// invalid-key audio is optional; only capture it when something was uploaded
			const [, invalidData] = invalidAudioRef.current.getResult();
			if (invalidData && invalidData.audio && invalidData.audio.length) {
				invalidAudio = invalidData.audio;
			} else {
				invalidAudio = [];
			}
		}

		if (isLiveAgent && transferAudioRef.current) {
			// live agent audio is optional; only capture it when something was uploaded
			const [, transferData] = transferAudioRef.current.getResult();
			if (transferData && transferData.audio && transferData.audio.length) {
				transferAudio = transferData.audio;
			} else {
				transferAudio = [];
			}
		}

		if (isLiveAgent && holdMusicRef.current) {
			// hold music is optional; only capture it when something was uploaded
			const [, holdData] = holdMusicRef.current.getResult();
			if (holdData && holdData.audio && holdData.audio.length) {
				holdMusic = holdData.audio;
			} else {
				holdMusic = [];
			}
		}

		if (Object.keys(_errors).length) {
			setErrors(_errors);
			return;
		}

		const data = {
			nodeKind: kind,
			title: isRoot ? 'Welcome' : label,
			label: isRoot ? '' : label,
			...(isRoot ? {} : { optionKey: String(optionKey) })
		};

		if (kind === NODE_KINDS.MENU) {
			data.audio = audio;
			data.noReplyAudio = noReplyAudio;
			data.invalidAudio = invalidAudio;
			// User enters seconds; convert to milliseconds for the API payload.
			data.noReplyTimeoutMs = Number(timeout) > 0 ? Number(timeout) * 1000 : DEFAULT_NO_REPLY_TIMEOUT_MS;
			data.noReplyMaxAttempts = Number(maxAttempts) || DEFAULT_NO_REPLY_MAX_ATTEMPTS;
		} else if (kind === NODE_KINDS.END) {
			data.audio = audio;
		} else if (isAIAgent) {
			data.agentType = agentType;
			// Only ElevenLabs carries an agent id; other providers send none (-> null).
			data.agentId = isElevenLabs ? agentId.trim() : '';
		} else if (isLiveAgent) {
			data.agentId = agentId.trim();
			data.audio = transferAudio;
			data.holdMusic = holdMusic;
		}

		submitNode(data);
	};

	return (
		<Dialog
			open
			onClose={closeDialog}
			aria-labelledby="form-dialog-title"
			classes={{ paper: 'm-24' }}
			style={{ marginTop: '2%' }}
			fullWidth
			maxWidth="xs"
			disableBackdropClick
		>
			<AppBar position="static" elevation={1}>
				<div
					className="flex flex-col items-center justify-center pb-10 text-20"
					style={{ paddingBottom: 20, paddingTop: 20 }}
				>
					{initialNode && !props.isAdd ? 'Edit Node' : 'Add Node'}
				</div>
			</AppBar>
			<DialogContent classes={{ root: 'p-24' }}>
				{!isRoot && (
					<div className="flex">
						<TextField
							className="mb-24"
							label="Key (optional)"
							name="optionKey"
							value={optionKey}
							variant="outlined"
							fullWidth
							onChange={e => setOptionKey(e.target.value.replace(/[^0-9]/g, '').slice(0, 1))}
							size="small"
							placeholder="e.g. 1"
							error={!!errors.optionKey}
							helperText={
								errors.optionKey ||
								'The key the caller presses to reach this option. Leave blank to keep the option but disable it.'
							}
						/>
					</div>
				)}

				{!isRoot && (
					<div className="flex">
						<TextField
							className="mb-24"
							label="Title"
							name="label"
							value={label}
							variant="outlined"
							required
							fullWidth
							onChange={e => setLabel(e.target.value)}
							size="small"
							error={!!errors.label}
							helperText={errors.label || ''}
						/>
					</div>
				)}

				<FormControl variant="outlined" size="small" fullWidth className={classes.formControl}>
					<InputLabel id="call-node-kind-label">Type</InputLabel>
					<Select
						labelId="call-node-kind-label"
						id="call-node-kind"
						value={kind}
						label="Type"
						onChange={e => setKind(e.target.value)}
					>
						{NODE_KIND_OPTIONS.map(opt => (
							<MenuItem key={opt.value} value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				{needsAudio && (
					<>
						<Typography className={classes.sectionTitle}>Audio</Typography>
						<CallAudioUploader ref={audioRef} required audio={initialNode?.audio || []} />
					</>
				)}

				{isMenu && (
					<>
						<Typography className={classes.sectionTitle}>No Reply Audio (optional)</Typography>
						<CallAudioUploader ref={noReplyAudioRef} audio={initialNode?.noReplyAudio || []} />

						<Typography className={classes.sectionTitle}>Invalid Key Audio (optional)</Typography>
						<CallAudioUploader ref={invalidAudioRef} audio={initialNode?.invalidAudio || []} />

						<div className="flex mt-16">
							<TextField
								label="No Reply Timeout (seconds)"
								name="timeout"
								value={timeout}
								variant="outlined"
								fullWidth
								type="number"
								onChange={e => setTimeoutValue(e.target.value)}
								size="small"
							/>
						</div>
						<div className="flex mt-16">
							<TextField
								label="No Reply Max Attempts"
								name="maxAttempts"
								value={maxAttempts}
								variant="outlined"
								fullWidth
								type="number"
								onChange={e => setMaxAttempts(e.target.value)}
								size="small"
							/>
						</div>
					</>
				)}

				{isAIAgent && (
					<>
						<FormControl
							variant="outlined"
							size="small"
							fullWidth
							className={classes.formControl}
							error={!!errors.agentType}
						>
							<InputLabel id="call-node-provider-label">Provider</InputLabel>
							<Select
								labelId="call-node-provider-label"
								id="call-node-provider"
								value={agentType}
								label="Provider"
								onChange={handleProviderChange}
							>
								{providers.length === 0 ? (
									<MenuItem value="" disabled>
										No providers found
									</MenuItem>
								) : (
									providers.map(p => (
										<MenuItem key={p.id} value={p.name}>
											{p.display_name || p.name}
										</MenuItem>
									))
								)}
							</Select>
							<Typography
								variant="caption"
								color={errors.agentType ? 'error' : 'textSecondary'}
								style={{ marginTop: 4 }}
							>
								{errors.agentType || 'The AI provider this option transfers to'}
							</Typography>
						</FormControl>

						{isElevenLabs && (
							<FormControl variant="outlined" size="small" fullWidth className={classes.formControl}>
								<InputLabel id="call-node-agent-label">Agent</InputLabel>
								<Select
									labelId="call-node-agent-label"
									id="call-node-agent"
									value={agentId}
									label="Agent"
									onChange={e => setAgentId(e.target.value)}
								>
									{agentsLoading ? (
										<MenuItem value="" disabled>
											Loading agents...
										</MenuItem>
									) : agents.length === 0 ? (
										<MenuItem value="" disabled>
											No agents found
										</MenuItem>
									) : (
										agents.map(a => (
											<MenuItem key={a.id} value={String(a.id)}>
												{a.agent_name || `Agent #${a.id}`}
											</MenuItem>
										))
									)}
								</Select>
								<Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
									The ElevenLabs agent to route the caller to
								</Typography>
							</FormControl>
						)}
					</>
				)}

				{isLiveAgent && (
					<>
						<FormControl variant="outlined" size="small" fullWidth className={classes.formControl}>
							<InputLabel id="call-node-human-label">Agent (optional)</InputLabel>
							<Select
								labelId="call-node-human-label"
								id="call-node-human"
								value={agentId}
								label="Agent (optional)"
								onChange={e => setAgentId(e.target.value)}
							>
								<MenuItem value="">
									<em>None (any available agent)</em>
								</MenuItem>
								{liveAgentsLoading ? (
									<MenuItem value="" disabled>
										Loading agents...
									</MenuItem>
								) : liveAgents.length === 0 ? (
									<MenuItem value="" disabled>
										No agents found
									</MenuItem>
								) : (
									liveAgents.map(a => (
										<MenuItem key={a.id} value={String(a.id)}>
											{a.username || `Agent #${a.id}`}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
						<div className={classes.sectionTitleRow}>
							<Typography className={classes.sectionTitleInRow}>Audio (optional)</Typography>
							<Tooltip
								arrow
								title="This is the audio that plays before the call is connected. One audio clip plays first, and once it finishes, the call is transferred to the agent."
								PopperProps={{ style: { zIndex: 13000 } }}
							>
								<Icon className={classes.infoIcon}>info</Icon>
							</Tooltip>
						</div>
						<CallAudioUploader ref={transferAudioRef} audio={initialNode?.audio || []} />

						<div className={classes.sectionTitleRow}>
							<Typography className={classes.sectionTitleInRow}>Hold Music (optional)</Typography>
							<Tooltip
								arrow
								title="This is the music that keeps playing in the background while the call is ongoing and being connected to the agent."
								PopperProps={{ style: { zIndex: 13000 } }}
							>
								<Icon className={classes.infoIcon}>info</Icon>
							</Tooltip>
						</div>
						<CallAudioUploader ref={holdMusicRef} audio={initialNode?.holdMusic || []} />
					</>
				)}
			</DialogContent>
			<DialogActions>
				<div className="px-16 my-10">
					<Button variant="contained" onClick={closeDialog} color="primary" size="small">
						Cancel
					</Button>
				</div>
				<ThemeProvider theme={theme}>
					<div className="mx-32 md:mx-24 my-10">
						<Button
							className={classes.margin}
							size="small"
							variant="contained"
							onClick={handleSubmit}
							color="primary"
						>
							{initialNode && !props.isAdd ? 'Update' : 'Create'}
						</Button>
					</div>
				</ThemeProvider>
			</DialogActions>
		</Dialog>
	);
};

export default CallNodeDialog;
